import { useFormState } from 'react-hook-form';
import { FormError } from 'shared/components/FormControl';

import ModalWrapper from 'dashboard/components/ModalWrapper';
import Form from 'dashboard/layouts/FormLayout/Form';
import { ModalBody, ModalContainer, ModalHeader } from 'dashboard/layouts/ModalLayout';
import Button from 'design-system/components/Button';
import Input from 'design-system/components/Input';
import Spacer from 'design-system/components/Spacer';
import { HStack } from 'design-system/components/Stack';
import { Label } from 'design-system/components/Text';
import TextArea from 'design-system/components/TextArea';
import { useFormSecurely } from 'lib/security/reactHookForm';

interface FormCreateApiKeyProps {
  defaultValues?: FormCreateApiKeyData;
  isOpen: boolean;
  handleSubmit: (data: FormCreateApiKeyData) => void;
  handleCancel?: () => void;
  handleCloseModal: () => void;
}

export const FormCreateApiKey = ({
  defaultValues,
  isOpen,
  handleSubmit,
  handleCancel = () => null,
  handleCloseModal,
}: FormCreateApiKeyProps) => {
  const {
    register,
    handleSubmit: handleReactHookFormSubmit,
    control,
    reset,
  } = useFormSecurely<FormCreateApiKeyData>({
    mode: 'onBlur',
    defaultValues,
  });

  const { isSubmitting, isDirty, errors } = useFormState({
    control,
  });
  const disabled = isSubmitting;

  const closeModal = () => {
    handleCloseModal();
    reset();
  };

  const submit = handleReactHookFormSubmit((data: FormCreateApiKeyData) => {
    handleSubmit(data);
    closeModal();
  });

  const cancel = () => {
    handleCancel();
    closeModal();
  };

  return (
    <ModalWrapper isOpen={isOpen} handleCloseModal={closeModal}>
      <Form
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            submit();
          }
        }}
      >
        <ModalContainer data-test='form--create-api-key__modal'>
          <ModalHeader data-test='form--create-api-key__modal__header' onClose={closeModal}>
            Create API key
          </ModalHeader>

          <ModalBody>
            <Label htmlFor='form--create-api-key__input--name' size='lg' weight='bold'>
              {' '}
              Name (required){' '}
            </Label>
            <Spacer height={1} />
            <Input
              {...register('name', {
                required: 'Please enter an API key name',
              })}
              id='form--create-api-key__input--name'
              data-test='form--create-api-key__input--name'
              type='text'
              placeholder='Name'
              hasError={!!errors['name']}
              disabled={disabled}
            />
            {errors['name'] && <FormError>{errors['name'].message}</FormError>}

            <Spacer height={2} />

            <Label htmlFor='form--create-api-key__textarea--note' size='lg' weight='bold'>
              {' '}
              Note{' '}
            </Label>
            <Spacer height={1} />
            <TextArea
              {...register('note')}
              id='form--create-api-key__textarea--note'
              data-test='form--create-api-key__textarea--note'
              disabled={disabled}
            />

            <Spacer height={4} />

            <HStack equal={true}>
              <Button
                type='button'
                variant='basic'
                onClick={cancel}
                data-test='form--create-api-key__button--cancel'
              >
                Cancel
              </Button>
              <Button
                type='button'
                disabled={disabled || !isDirty}
                onClick={submit}
                data-test='form--create-api-key__button--submit'
              >
                Create
              </Button>
            </HStack>
          </ModalBody>
        </ModalContainer>
      </Form>
    </ModalWrapper>
  );
};

export interface FormCreateApiKeyData {
  name: string;
  note: string;
}
